import React, { useRef, useState, useEffect, useImperativeHandle, useCallback } from 'react';
import TextField from '@material-ui/core/TextField';

// Helper to check if HTML content is visually empty
export const isWysiwygEmpty = (html) => {
  if (!html) return true;
  if (typeof html !== 'string') return false;
  if (/<(img|video|audio|iframe|table|hr)\b/i.test(html)) return false;
  const clean = html
    .replace(/<[^>]+>/g, '')
    .replace(/&(nbsp|amp|quot|lt|gt);/g, ' ')
    .replace(/&#\d+;/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();
  return clean.length === 0;
};

// Sanitize bullets: eliminate duplicate bullets and ensure single space after bullet symbol
export const sanitizeBulletText = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    // 1. Collapse multiple consecutive/spaced bullet characters into a single bullet
    .replace(/([•◦▪])(?:\s*[•◦▪])+/g, '$1')
    // 2. Ensure bullet symbol is separated from text by a single space (e.g. •aaaa -> • aaaa)
    .replace(/^(\s*[•◦▪])([^\s•◦▪])/gm, '$1 $2')
    // 3. Ensure level 1 bullet has leading space if it was flush to left edge (• aaaa ->     • aaaa)
    .replace(/^(•\s)/gm, '    $1');
};

// Bullet hierarchy helpers
// Level 1: '    • '          (4 spaces)
// Level 2: '        ◦ '      (8 spaces)
// Level 3: '            ▪ '  (12 spaces)
// Level N: (N * 4) spaces + bullet
export const getBulletLevel = (spaces, bulletChar) => {
  const spaceCount = (spaces || '').length;
  if (bulletChar === '•') return 1;
  if (bulletChar === '◦') return 2;
  if (bulletChar === '▪') return Math.max(3, Math.floor(spaceCount / 4));
  return Math.max(1, Math.floor(spaceCount / 4));
};

export const getPrefixForLevel = (level) => {
  if (level <= 0) return '';
  if (level === 1) return '    • ';
  if (level === 2) return '        ◦ ';
  const indentCount = level * 4;
  return ' '.repeat(indentCount) + '▪ ';
};

// Custom contentEditable input component passed to Material-UI TextField via InputProps.inputComponent
const WysiwygContentInput = React.forwardRef((props, ref) => {
  const {
    value,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    className,
    style,
    disabled,
    readOnly,
    rows,
    autoFocus,
    ...other
  } = props;

  const contentRef = useRef(null);
  const isTypingRef = useRef(false);

  // Helper to check if HTML content is visually empty
  const checkIsEmpty = (html) => isWysiwygEmpty(html);

  const [isEmpty, setIsEmpty] = useState(checkIsEmpty(value));

  // Expose focus and value to Material-UI InputBase
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (contentRef.current) {
        contentRef.current.focus();
      }
    },
    value: value || '',
    scrollWidth: contentRef.current ? contentRef.current.scrollWidth : 0,
    scrollHeight: contentRef.current ? contentRef.current.scrollHeight : 0,
  }));

  // Sync external value with editor content when not actively typing
  useEffect(() => {
    if (contentRef.current && !isTypingRef.current) {
      const currentHtml = contentRef.current.innerHTML;
      const targetVal = sanitizeBulletText(value || '');
      if (currentHtml !== targetVal) {
        contentRef.current.innerHTML = targetVal;
        setIsEmpty(checkIsEmpty(targetVal));
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (!contentRef.current) return;
    isTypingRef.current = true;
    const html = contentRef.current.innerHTML;
    const textEmpty = checkIsEmpty(html);
    setIsEmpty(textEmpty);

    if (onChange) {
      onChange({
        target: {
          value: textEmpty ? '' : html,
          name: props.name || 'description',
        },
      });
    }

    setTimeout(() => {
      isTypingRef.current = false;
    }, 0);
  }, [onChange, props.name]);

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    if (text) {
      const cleaned = sanitizeBulletText(text);
      document.execCommand('insertText', false, cleaned);
      handleInput();
    }
  };

  const handleKeyDown = (e) => {
    // Rich Text Formatting Shortcuts: Ctrl+B, Ctrl+I, Ctrl+U
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      const key = e.key.toLowerCase();
      if (key === 'b') {
        e.preventDefault();
        document.execCommand('bold', false, null);
        handleInput();
        return;
      }
      if (key === 'i') {
        e.preventDefault();
        document.execCommand('italic', false, null);
        handleInput();
        return;
      }
      if (key === 'u') {
        e.preventDefault();
        document.execCommand('underline', false, null);
        handleInput();
        return;
      }
    }

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !contentRef.current) return;
    const range = sel.getRangeAt(0);

    // 1. Space key: convert "- " or "* " or ". " at line start to bullet
    if (e.key === ' ' || e.code === 'Space') {
      if (range.collapsed && typeof sel.modify === 'function') {
        const cloneRange = range.cloneRange();
        sel.removeAllRanges();
        sel.addRange(cloneRange);
        sel.modify('extend', 'backward', 'lineboundary');
        const textBefore = sel.toString();

        const match = textBefore.match(/^(\s*)([-*•]|\.)\s*$/);
        if (match && ['-', '*', '.'].includes(match[2])) {
          e.preventDefault();
          const spaceCount = match[1].length;
          const level = spaceCount >= 8 ? 3 : spaceCount >= 4 ? 2 : 1;
          const prefix = getPrefixForLevel(level);

          document.execCommand('insertText', false, prefix);
          handleInput();
          return;
        } else {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
      return;
    }

    // 2. Tab key: convert "- + Tab" to bullet OR indent/outdent existing bullet
    if (e.key === 'Tab') {
      e.preventDefault();
      if (range.collapsed && typeof sel.modify === 'function') {
        const cloneBefore = range.cloneRange();
        sel.removeAllRanges();
        sel.addRange(cloneBefore);
        sel.modify('extend', 'backward', 'lineboundary');
        const textBefore = sel.toString();

        const cloneAfter = range.cloneRange();
        sel.removeAllRanges();
        sel.addRange(cloneAfter);
        sel.modify('extend', 'forward', 'lineboundary');
        const textAfter = sel.toString();

        const fullLine = textBefore + textAfter;

        // Check if user typed "-" or "*" or "." right before cursor
        const dashMatch = textBefore.match(/^(\s*)([-*•]|\.)\s*$/);
        if (dashMatch && ['-', '*', '.'].includes(dashMatch[2]) && !e.shiftKey) {
          const spaceCount = dashMatch[1].length;
          const level = spaceCount >= 8 ? 3 : spaceCount >= 4 ? 2 : 1;
          const prefix = getPrefixForLevel(level);
          document.execCommand('insertText', false, prefix);
          handleInput();
          return;
        }

        const bulletMatch = fullLine.match(/^(\s*)([•◦▪])\s*(.*)$/);
        if (bulletMatch) {
          const currentLevel = getBulletLevel(bulletMatch[1], bulletMatch[2]);
          let targetLevel;
          if (e.shiftKey) {
            targetLevel = currentLevel - 1;
          } else {
            targetLevel = Math.min(currentLevel + 1, 6);
          }

          const newPrefix = getPrefixForLevel(targetLevel);
          const lineContent = bulletMatch[3] || '';

          // Select entire line and replace with newPrefix + lineContent
          sel.removeAllRanges();
          sel.addRange(range);
          sel.modify('move', 'backward', 'lineboundary');
          sel.modify('extend', 'forward', 'lineboundary');
          document.execCommand('insertText', false, newPrefix + lineContent);

          // Position caret after prefix
          sel.modify('move', 'backward', 'lineboundary');
          for (let i = 0; i < newPrefix.length; i++) {
            sel.modify('move', 'forward', 'character');
          }

          handleInput();
          return;
        } else {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }

      if (!e.shiftKey) {
        document.execCommand('insertText', false, '    ');
        handleInput();
        return;
      }
      return;
    }

    // 3. Enter key: continue bullet or exit empty bullet
    if (e.key === 'Enter') {
      if (range.collapsed && typeof sel.modify === 'function') {
        const cloneBefore = range.cloneRange();
        sel.removeAllRanges();
        sel.addRange(cloneBefore);
        sel.modify('extend', 'backward', 'lineboundary');
        const textBefore = sel.toString();

        const cloneAfter = range.cloneRange();
        sel.removeAllRanges();
        sel.addRange(cloneAfter);
        sel.modify('extend', 'forward', 'lineboundary');
        const textAfter = sel.toString();

        const fullLine = textBefore + textAfter;
        const bulletMatch = fullLine.match(/^(\s*)([•◦▪])\s*(.*)$/);

        if (bulletMatch) {
          e.preventDefault();
          const indent = bulletMatch[1];
          const bulletChar = bulletMatch[2];
          const userContent = (bulletMatch[3] || '').replace(/[•◦▪\s]/g, '');

          if (userContent.length > 0) {
            // Line has text: Continue bullet on new line at the SAME level
            sel.removeAllRanges();
            sel.addRange(range);

            const currentLevel = getBulletLevel(indent, bulletChar);
            const prefix = getPrefixForLevel(currentLevel);

            document.execCommand('insertText', false, '\n' + prefix);
            handleInput();
            return;
          } else {
            // Empty bullet line (only bullet symbol and whitespace, or duplicate bullets)
            const currentLevel = getBulletLevel(indent, bulletChar);

            if (currentLevel > 1) {
              const targetLevel = currentLevel - 1;
              const newPrefix = getPrefixForLevel(targetLevel);
              sel.removeAllRanges();
              sel.addRange(range);
              sel.modify('move', 'backward', 'lineboundary');
              sel.modify('extend', 'forward', 'lineboundary');
              document.execCommand('insertText', false, newPrefix);
              handleInput();
              return;
            } else {
              // Level 1: Clear the line completely to exit bullet list
              sel.removeAllRanges();
              sel.addRange(range);
              sel.modify('move', 'backward', 'lineboundary');
              sel.modify('extend', 'forward', 'lineboundary');
              document.execCommand('delete');
              handleInput();
              return;
            }
          }
        } else {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    }

    // 4. Backspace key: remove bullet cleanly on empty bullet line
    if (e.key === 'Backspace') {
      if (range.collapsed && typeof sel.modify === 'function') {
        const cloneBefore = range.cloneRange();
        sel.removeAllRanges();
        sel.addRange(cloneBefore);
        sel.modify('extend', 'backward', 'lineboundary');
        const textBefore = sel.toString();

        const cloneAfter = range.cloneRange();
        sel.removeAllRanges();
        sel.addRange(cloneAfter);
        sel.modify('extend', 'forward', 'lineboundary');
        const textAfter = sel.toString();

        const bulletMatch = textBefore.match(/^(\s*)([•◦▪])\s*$/);
        if (bulletMatch && textAfter.trim() === '') {
          e.preventDefault();
          const indent = bulletMatch[1];
          const bulletChar = bulletMatch[2];
          const currentLevel = getBulletLevel(indent, bulletChar);

          if (currentLevel > 1) {
            const targetLevel = currentLevel - 1;
            const newPrefix = getPrefixForLevel(targetLevel);
            sel.removeAllRanges();
            sel.addRange(range);
            sel.modify('move', 'backward', 'lineboundary');
            sel.modify('extend', 'forward', 'lineboundary');
            document.execCommand('insertText', false, newPrefix);
            handleInput();
            return;
          } else {
            // Level 1: Delete bullet and space cleanly
            sel.removeAllRanges();
            sel.addRange(range);
            sel.modify('move', 'backward', 'lineboundary');
            sel.modify('extend', 'forward', 'lineboundary');
            document.execCommand('delete');
            handleInput();
            return;
          }
        } else {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <div
      ref={contentRef}
      id={props.id}
      className={`tm-wysiwyg-content ${className || ''}`}
      contentEditable={!disabled && !readOnly}
      suppressContentEditableWarning
      data-empty={isEmpty}
      data-placeholder={placeholder || ''}
      onFocus={(e) => {
        if (onFocus) onFocus(e);
      }}
      onBlur={(e) => {
        if (contentRef.current) {
          const raw = contentRef.current.innerHTML;
          const sanitized = sanitizeBulletText(raw);
          if (sanitized !== raw) {
            contentRef.current.innerHTML = sanitized;
          }
        }
        handleInput();
        if (onBlur) onBlur(e);
      }}
      onPaste={handlePaste}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      style={{
        position: 'relative',
        minHeight: 110,
        maxHeight: 400,
        outline: 'none',
        width: '100%',
        textAlign: 'left',
        direction: 'ltr',
        cursor: 'text',
        ...style,
      }}
      {...other}
    />
  );
});

// Full Material-UI TextField with WysiwygContentInput as its inputComponent
const WysiwygDescriptionEditor = ({
  value = '',
  onChange,
  onFocus,
  onBlur,
  label = 'Description',
  placeholder = 'Enter description...',
  error = false,
  helperText = '',
  id = 'description',
  name = 'description',
  rows = 4,
  minHeight,
  className = '',
  InputLabelProps,
  InputProps,
  inputProps,
  ...otherProps
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const hasContent = !isWysiwygEmpty(value);
  const isShrunk = (InputLabelProps && typeof InputLabelProps.shrink !== 'undefined')
    ? InputLabelProps.shrink
    : Boolean(isFocused || hasContent);

  const handleFocus = (e) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  return (
    <TextField
      fullWidth
      multiline
      rows={rows}
      variant="outlined"
      id={id}
      name={name}
      label={label}
      placeholder={placeholder}
      value={value || ''}
      onChange={(e) => {
        if (onChange) {
          onChange(e.target.value);
        }
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      error={Boolean(error)}
      helperText={helperText}
      className={`tm-wysiwyg-mui-field ${error ? 'input-error' : ''} ${className}`}
      InputLabelProps={{
        shrink: isShrunk,
        ...InputLabelProps,
      }}
      InputProps={{
        inputComponent: WysiwygContentInput,
        onFocus: handleFocus,
        onBlur: handleBlur,
        style: minHeight ? { minHeight, ...(InputProps && InputProps.style) } : (InputProps && InputProps.style),
        ...InputProps,
      }}
      inputProps={{
        placeholder: placeholder,
        id: id,
        name: name,
        onFocus: handleFocus,
        onBlur: handleBlur,
        style: minHeight ? { minHeight: minHeight - 20, ...(inputProps && inputProps.style) } : (inputProps && inputProps.style),
        ...inputProps,
      }}
      {...otherProps}
    />
  );
};

export default WysiwygDescriptionEditor;
